import {useMemo} from 'react';
import {UseQueryOptions, UseQueryResult} from '@tanstack/react-query';
import {Organization} from '@tinct/core-api-models';
import {ApiCollectionResponse, GetCollectionQueryParams, QueryParams} from '../../types';
import {ApiError} from '../../api';
import {useGetUserOrganizations} from './use-get-user-organizations';

type GetAgencyChildOrganizationsStaticParams = {
  userId: string;
  agencyId: string;
};

export type GetAgencyChildOrganizationsParams = GetCollectionQueryParams<
  Organization,
  GetAgencyChildOrganizationsStaticParams
> & {
  filters?: QueryParams;
};

/**
 * Upper bound on the workspaces fetched before filtering. The API exposes no
 * `parent_id` filter yet, so the children of an agency are derived client-side
 * from the connected user's organizations.
 */
const CHILD_ORGANIZATIONS_FETCH_LIMIT = 200;

/**
 * Lists the child workspaces of an agency, as seen by the connected user.
 *
 * TEMPORARY: filters client-side on `parent.id`. Pagination is emulated on the
 * filtered set (`offset`/`limit` from the caller are applied locally), so the
 * page count is only exact while the user has fewer than
 * `CHILD_ORGANIZATIONS_FETCH_LIMIT` organizations. Switch to a server-side
 * `parent_id` filter once the API provides one.
 */
export const useGetAgencyChildOrganizations = (
  params: GetAgencyChildOrganizationsParams,
  options?: Omit<
    UseQueryOptions<ApiCollectionResponse<Organization>, ApiError>,
    'queryKey'
  >,
): UseQueryResult<ApiCollectionResponse<Organization>, ApiError> => {
  const {staticParams, fields, filters, offset = 0, limit = 20} = params;
  const agencyId = staticParams?.agencyId;

  const result = useGetUserOrganizations(
    {
      staticParams: {userId: staticParams?.userId ?? ''},
      // `parent.id` is what the filter keys on; the rest is the caller's.
      fields: {...fields, parent: {fields: {id: null, name: null, type: null}}},
      filters,
      offset: 0,
      limit: CHILD_ORGANIZATIONS_FETCH_LIMIT,
    },
    options,
  );

  const data = useMemo(() => {
    if (!result.data) return undefined;
    const children = result.data.data.filter(
      (organization) => organization.parent?.id === agencyId,
    );
    return {
      data: children.slice(offset, offset + limit),
      pagination: {count: children.length, offset, limit},
    };
  }, [result.data, agencyId, offset, limit]);

  return {...result, data} as UseQueryResult<ApiCollectionResponse<Organization>, ApiError>;
};
